import { createFileRoute, Outlet } from '@tanstack/react-router'
import { AppShellLayout } from '@/components/app-shell/AppShellLayout'
import { useAuth } from '@/lib/auth/auth-context'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { requireAuth } from '@/lib/auth/route-guards'
import type { RouterAuthContext } from '@/lib/auth/router-context'

export const Route = createFileRoute('/app')({
  head: () => ({
    meta: [
      {
        name: 'robots',
        content: 'noindex, nofollow',
      },
    ],
  }),

  // Route-level auth guard using router context
  beforeLoad: async ({ context, location }) => {
    // This will redirect to /auth/login if not authenticated
    // or suspend if still loading
    await requireAuth(context as RouterAuthContext, location.pathname)
  },

  component: AppLayout,
})

function AppLayout() {
  const auth = useAuth()

  // Show loading while checking auth (provides UX feedback while beforeLoad runs)
  if (auth.isLoading || auth.isSessionLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-purple-500 border-r-transparent" />
          <p className="mt-4 text-slate-600 dark:text-slate-400">Loading...</p>
        </div>
      </div>
    )
  }

  // If we reach here, auth check passed in beforeLoad
  // User is authenticated and session is loaded
  return (
    <ErrorBoundary level="route">
      <AppShellLayout>
        <Outlet />
      </AppShellLayout>
    </ErrorBoundary>
  )
}
